import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CallSession, registerInboundCall } from '../services/callingService';
import styles from './CallCenter.module.css';

/**
 * Landing page for the 3CX "Open Contact in Custom CRM" URL. It records the inbound call,
 * then sends the employee straight to the matched conversation or contact.
 */
export const InboundCallPopPage: React.FC = () => {
  const { t } = useTranslation('calling');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const phoneNumber = (searchParams.get('phoneNumber') || '').trim();
  const callerName = (searchParams.get('callerName') || '').trim();
  const externalCallId = (searchParams.get('callId') || '').trim();

  const [session, setSession] = useState<CallSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;

    if (!phoneNumber) {
      setError(t('popMissingNumber'));
      return;
    }

    const register = async () => {
      try {
        const created = await registerInboundCall({
          phoneNumber,
          callerName: callerName || undefined,
          externalCallId: externalCallId || undefined,
        });
        setSession(created);

        if (created.conversationId) {
          navigate(`/conversations/${created.conversationId}`, { replace: true });
        } else if (created.contactId) {
          navigate(`/contacts/${created.contactId}`, { replace: true });
        }
      } catch (registerError) {
        setError(registerError instanceof Error ? registerError.message : t('popMissingNumber'));
      }
    };

    void register();
  }, [phoneNumber, callerName, externalCallId, navigate, t]);

  return (
    <section className={styles.popPage}>
      <h2>{t('popTitle')}</h2>
      <p>{callerName || phoneNumber}</p>

      {!session && !error && <p>{t('popMatching')}</p>}
      {error && <p>{error}</p>}
      {session && !session.contactId && <p>{t('popNoContact', { phoneNumber })}</p>}

      <div className={styles.popActions}>
        {session && !session.contactId && phoneNumber && (
          <button
            type="button"
            className={`${styles.button} ${styles.subtle}`}
            onClick={() => navigate(`/contacts/add?phone=${encodeURIComponent(phoneNumber)}`)}
          >
            {t('popCreateContact')}
          </button>
        )}
        {session?.contactId && (
          <button
            type="button"
            className={`${styles.button} ${styles.subtle}`}
            onClick={() => navigate(`/contacts/${session.contactId}`)}
          >
            {t('popOpenContact')}
          </button>
        )}
      </div>
    </section>
  );
};

export default InboundCallPopPage;
